<template>
  <nav class="mega-nav">
    <ul
      v-for="(column, index) of columns"
      :key="getKey(column, index)"
      class="mega-nav__list flex flex--col"
    >
      <!-- Categories -->
      <li
        v-for="tier2 of column"
        :key="getTierKey(tier2, index)"
        class="mega-nav__sub-list flex flex--col"
      >
        <!-- Category -->
        <component
          :is="tier2.url === '#' ? 'span' : 'a'"
          class="mega-nav__link text-body-s-medium"
          :href="tier2.url === '#' ? undefined : tier2.url"
          :data-json="JSON.stringify(tier2)"
          @mouseenter="updateImage(tier2)"
          v-text="tier2.title"
        />

        <!-- Subcategories -->
        <ul
          v-if="tier2.items?.length"
          class="mega-nav__sub-list flex flex--col"
        >
          <li
            v-for="tier3 of tier2.items"
            :key="getTierKey(tier3, index)"
          >
            <a
              class="mega-nav__link mega-nav__link-small text-body-s-regular"
              :href="tier3.url"
              v-text="tier3.title"
            />
          </li>
        </ul>
      </li>
    </ul>

    <menu-image :image="currentImage" />
  </nav>
</template>

<script>

/**
 * Vue: Mega nav (mega-nav)
 * -----------------------------------------------------------------------------
 * Statically rendered list of links with supporting image.
 *
 * @param {Number} columnCounts.tier2 - Max number of tier 2 items in a column
 * @param {Number} columnCounts.tier3 - Max number of tier 3 items in a column
 * @param {Array} items - Navigation items, with a handle, title and url.
 * @param {Object} image - Supporting image with optional url and alt.
 * @param {Number} parentHoverRefresh - Reset the current image to the main
 * default image from props if this number changes.
 */

import MenuImage from '~global/menu-image/menu-image'
import { convertToHandle } from '~/helpers/convert'

export default {
  name: 'MegaNav',

  components: {
    MenuImage,
  },

  props: {
    columnCounts: {
      type: Object,
      default: () => ({
        tier2: 5,
        tier3: 6,
      }),
    },
    items: {
      type: Array,
      default: () => [],
    },
    image: {
      type: Object,
      default: () => ({
        alt: '',
        src: '',
        url: '',
        linkText: '',
        headingText: '',
      }),
    },
    parentHoverRefresh: {
      type: Number,
      default: -1,
    },
  },

  data() {
    return {
      currentImage: this.image,
    }
  },

  computed: {

    /**
     * Compute columns
     * @returns {[]}
     */
    columns() {
      const columns = []
      let column = []
      let tier3Count = 0
      this.items.forEach((tier2) => {
        tier3Count += tier2.items?.length || 0

        /**
         * If tier 3 or tier 2 hits the limit, save the old column and
         * start a new column
         */
        if (
          tier3Count > this.columnCounts.tier3 ||
          column.length >= this.columnCounts.tier2
        ) {
          if (column.length) {
            columns.push(column.slice())
          }
          column = [tier2]
          tier3Count = tier2.items?.length || 0
        } else {
          column.push(tier2)
        }
      })

      /**
       * Add remaining links to a new column or the last column (if there are
       * 3 columns already)
       */
      if (column.length) {
        if (columns.length === 3) {
          columns[2] = columns[2].concat(column)
        } else {
          columns.push(column)
        }
      }

      return columns
    },
  },

  watch: {

    /**
     * Reset current image when tier one menu item is being hovered
     */
    parentHoverRefresh(newKey, oldKey) {
      if (newKey === oldKey || JSON.stringify(this.image) === JSON.stringify(this.currentImage)) {
        return
      }
      this.currentImage = this.image
    },
  },

  methods: {

    /**
     * Generate key for Vue loop
     * @param {Array} column - List of tiers in the same column
     * @param index - Index in loop
     * @returns {string}
     */
    getKey(column, index) {
      return `${column.map((tier) => convertToHandle(tier.title))
        .join('-')}-${index}`
    },

    /**
     * Get key for tier loops
     * @param {{title: string}} tier
     * @param {Number} index
     */
    getTierKey(tier, index) {
      return `${convertToHandle(tier.title)}-${index}`
    },

    /**
     * Updates the current image when hovering over a tier two item.
     * @param {Object} tier2 - The tier two item.
     */
    updateImage(tier2) {
      if (!tier2?.image?.src) {
        return
      }
      this.currentImage = tier2.image
    },
  },
}
</script>
